import { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useAuth } from '../../../context/AuthContext';
import { getPaymentRequests } from '../../../services/settlementService';

const REQUEST_STATUS = {
  pending: { label: '대기', bg: '#F59E0B', color: '#fff' },
  completed: { label: '완료', bg: '#22C55E', color: '#fff' },
  confirming: { label: '확인중', bg: '#3B82F6', color: '#fff' },
  rejected: { label: '거절', bg: '#EF4444', color: '#fff' },
};

const PaymentRequest = () => {
  const { userData } = useAuth();
  const [statusFilter, setStatusFilter] = useState('all');
  const [selected, setSelected] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!userData?.uid) return;
    setLoading(true);
    getPaymentRequests(userData.uid)
      .then(setItems)
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [userData?.uid]);

  const filtered = items.filter((d) => statusFilter === 'all' || d.status === statusFilter);

  return (
    <Container>
      <Title>결제 요청 수신</Title>
      <SubText>본사에서 금액을 지정하여 보낸 결제 요청 내역입니다.</SubText>

      <FilterRow>
        <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          <option value="all">전체</option>
          {Object.entries(REQUEST_STATUS).map(([key, val]) => (
            <option key={key} value={key}>{val.label}</option>
          ))}
        </Select>
        <CountText>{filtered.length}건</CountText>
      </FilterRow>

      {loading ? (
        <EmptyText>불러오는 중...</EmptyText>
      ) : filtered.length === 0 ? (
        <EmptyText>결제 요청이 없습니다.</EmptyText>
      ) : (
        <CardList>
          {filtered.map((d) => (
            <Card key={d.id} onClick={() => setSelected(d)}>
              <CardTop>
                <CardId>{d.label || d.id}</CardId>
                <Badge $bg={REQUEST_STATUS[d.status]?.bg || '#6B7280'}>{REQUEST_STATUS[d.status]?.label || d.status}</Badge>
              </CardTop>
              <CardService>{d.memo || d.label || '-'}</CardService>
              <CardBottom>
                <CardAmount>{(d.amount || 0).toLocaleString()}원</CardAmount>
                <CardDate>{d.date}</CardDate>
              </CardBottom>
            </Card>
          ))}
        </CardList>
      )}

      {selected && (
        <SheetOverlay onClick={() => setSelected(null)}>
          <BottomSheet onClick={(e) => e.stopPropagation()}>
            <SheetHandle />
            <SheetTitle>결제 요청 상세</SheetTitle>
            <SheetBody>
              <InfoRow><InfoLabel>요청번호</InfoLabel><InfoValue>{selected.id}</InfoValue></InfoRow>
              <InfoRow><InfoLabel>금액</InfoLabel><InfoValue style={{ fontWeight: 700 }}>{(selected.amount || 0).toLocaleString()}원</InfoValue></InfoRow>
              <InfoRow><InfoLabel>요청일</InfoLabel><InfoValue>{selected.date}</InfoValue></InfoRow>
              <InfoRow><InfoLabel>상태</InfoLabel><InfoValue><Badge $bg={REQUEST_STATUS[selected.status]?.bg || '#6B7280'}>{REQUEST_STATUS[selected.status]?.label || selected.status}</Badge></InfoValue></InfoRow>
              {selected.memo && <InfoRow><InfoLabel>메모</InfoLabel><InfoValue>{selected.memo}</InfoValue></InfoRow>}
              {selected.method && <InfoRow><InfoLabel>결제수단</InfoLabel><InfoValue>{selected.method}</InfoValue></InfoRow>}
            </SheetBody>
            <SheetActions>
              {selected.status === 'pending' && (
                <PrimaryBtn onClick={() => { alert('결제하기 (더미)'); setSelected(null); }}>결제하기</PrimaryBtn>
              )}
              <SecondaryBtn onClick={() => setSelected(null)}>닫기</SecondaryBtn>
            </SheetActions>
          </BottomSheet>
        </SheetOverlay>
      )}
    </Container>
  );
};

export default PaymentRequest;

// --- Styled ---
const Container = styled.div``;
const Title = styled.h1`font-size:18px;font-weight:600;margin-bottom:4px;`;
const SubText = styled.p`font-size:13px;color:${({ theme }) => theme.colors.textSecondary};margin-bottom:20px;`;
const FilterRow = styled.div`display:flex;gap:12px;align-items:center;margin-bottom:16px;`;
const Select = styled.select`padding:8px 12px;border:1px solid ${({ theme }) => theme.colors.border};border-radius:${({ theme }) => theme.radius.sm};font-size:13px;background:#fff;`;
const CountText = styled.span`margin-left:auto;font-size:13px;color:${({ theme }) => theme.colors.textSecondary};`;

const CardList = styled.div`display:flex;flex-direction:column;gap:12px;max-width:680px;`;
const Card = styled.div`
  background:${({ theme }) => theme.colors.bgWhite};
  border-radius:${({ theme }) => theme.radius.md};
  box-shadow:${({ theme }) => theme.shadow.sm};
  padding:16px;cursor:pointer;
  &:active{background:#F9FAFB;}
`;
const CardTop = styled.div`display:flex;justify-content:space-between;align-items:center;margin-bottom:8px;`;
const CardId = styled.span`font-size:12px;color:${({ theme }) => theme.colors.textSecondary};`;
const CardService = styled.div`font-size:14px;font-weight:500;margin-bottom:10px;`;
const CardBottom = styled.div`display:flex;justify-content:space-between;align-items:center;`;
const CardAmount = styled.span`font-size:15px;font-weight:700;`;
const CardDate = styled.span`font-size:12px;color:${({ theme }) => theme.colors.textSecondary};`;

const Badge = styled.span`display:inline-block;padding:4px 10px;border-radius:6px;font-size:12px;font-weight:400;color:#fff;background:${({ $bg }) => $bg || '#9CA3AF'};`;

// --- Bottom Sheet ---
const SheetOverlay = styled.div`position:fixed;top:0;left:0;right:0;bottom:0;background:rgba(0,0,0,0.4);z-index:1000;display:flex;align-items:flex-end;justify-content:center;
  @media(min-width:${({ theme }) => theme.breakpoints.mobile}){align-items:center;}
`;
const BottomSheet = styled.div`
  background:#fff;width:100%;border-radius:16px 16px 0 0;padding:20px;max-height:80vh;overflow-y:auto;
  @media(min-width:${({ theme }) => theme.breakpoints.mobile}){max-width:480px;border-radius:${({ theme }) => theme.radius.lg};margin:0 auto;}
`;
const SheetHandle = styled.div`width:40px;height:4px;border-radius:2px;background:#D1D5DB;margin:0 auto 16px;
  @media(min-width:${({ theme }) => theme.breakpoints.mobile}){display:none;}
`;
const SheetTitle = styled.h2`font-size:16px;font-weight:600;margin-bottom:16px;`;
const SheetBody = styled.div`display:flex;flex-direction:column;gap:10px;`;
const InfoRow = styled.div`display:flex;justify-content:space-between;align-items:center;padding:4px 0;`;
const InfoLabel = styled.span`font-size:13px;color:${({ theme }) => theme.colors.textSecondary};`;
const InfoValue = styled.span`font-size:13px;font-weight:500;text-align:right;`;
const SheetActions = styled.div`margin-top:20px;display:flex;flex-direction:column;gap:8px;`;
const PrimaryBtn = styled.button`width:100%;height:48px;border:none;border-radius:${({ theme }) => theme.radius.md};background:${({ theme }) => theme.colors.primary};color:#fff;font-size:15px;font-weight:600;cursor:pointer;`;
const SecondaryBtn = styled.button`width:100%;height:44px;border:1px solid ${({ theme }) => theme.colors.border};border-radius:${({ theme }) => theme.radius.md};background:#fff;font-size:14px;cursor:pointer;`;
const EmptyText = styled.div`padding:60px 0;text-align:center;color:${({ theme }) => theme.colors.textSecondary};font-size:13px;`;
